/**
 * AuthContext - Authentication state management
 *
 * Integrates with @tonnie/sdk for authentication.
 * Provides extended user profile data alongside SDK auth.
 *
 * PRODUCTION: Uses real Clerk authentication via SDK.
 * DEVELOPMENT: Falls back to mock auth when VITE_CLERK_PUBLISHABLE_KEY='mock'
 *
 * @migration For new code, prefer using useAuth() from @tonnie/sdk directly.
 * This context provides backward compatibility + extended profile data.
 */

import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useErrorCapture } from '@tonnie/observability';
import {
  useCreateJourney,
  useCurrentUser,
  useJourneys,
  useAuth as useSdkAuth,
  useSubscription,
  useUpdateProfile,
  useUserProfile,
} from '@tonnie/sdk';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'wouter';

// Import types from centralized location
import type { IAuthContextValue, IJourneyInstance, IUser } from '../types/auth.types';

// Re-export types for backward compatibility
export type {
  FamilyMember,
  ICreateJourneyInput,
  IFamilyMember,
  IJourneyInstance,
  IUser,
  JourneyInstance,
  // Deprecated aliases
  User,
} from '../types/auth.types';

const AuthContext = createContext<IAuthContextValue | undefined>(undefined);

// ═══════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Map SDK/Clerk role to journey-web UserRole.
 * The SDK's AuthProvider defaults to 'citizen' from Clerk publicMetadata.
 * In journey-web, 'citizen' and any unrecognized role map to 'talent'.
 */
function mapSdkRoleToUserRole(sdkRole: string | undefined): IUser['role'] {
  if (sdkRole === 'family_master') return 'family_master';
  if (sdkRole === 'family_member') return 'family_member';
  // 'citizen', 'talent', undefined, and any other value → 'talent'
  return 'talent';
}

function buildJourneyInstances(journeys: unknown[]): IJourneyInstance[] {
  if (!journeys || !Array.isArray(journeys)) return [];

  return (journeys as Record<string, unknown>[]).map((j) => ({
    id: (j.id as string) || '',
    scenario: (j.scenario as IJourneyInstance['scenario']) || 'pre_immigration',
    // origin_country may legitimately be NULL when the journey is in
    // 'pending_setup' lifecycle (cornerstone refactor 2026-05).
    originCountry: (j.originCountry as string | null | undefined) ?? null,
    destinationCountry: (j.destinationCountry as string) || '',
    label: (j.label as string | null | undefined) ?? null,
    scope: (j.scope as IJourneyInstance['scope']) || {
      checkup: true,
      teleportal: true,
      qultureup: true,
      care: true,
      passport: true,
    },
    fundingSource: (j.fundingSource as IJourneyInstance['fundingSource']) ?? 'self_free',
    lifecycleState: (j.lifecycleState as IJourneyInstance['lifecycleState']) ?? 'draft',
    createdAt: (j.createdAt as string) || new Date().toISOString(),
    sponsorName: j.sponsorName as string | undefined,
    // Carried through verbatim: each company's own relationship state, never a
    // default of `'live'` (W8.1 · F45).
    companies: (j.companies as IJourneyInstance['companies'] | undefined) ?? [],
  }));
}

// ═══════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════

export function AuthProvider({ children }: { children: ReactNode }) {
  return <AuthProviderCore>{children}</AuthProviderCore>;
}

function AuthProviderCore({ children }: { children: ReactNode }) {
  const { signOut: clerkSignOut } = useClerkAuth();
  const [, setLocation] = useLocation();
  const { captureException } = useErrorCapture();

  // SDK authentication hooks
  const sdkAuth = useSdkAuth();
  const { isLoading: userLoading } = useCurrentUser();
  const { data: userProfile } = useUserProfile();
  const { data: journeys } = useJourneys();
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription();
  const updateProfileMutation = useUpdateProfile();
  const createJourneyMutation = useCreateJourney();

  // Determine loading state.
  // Only wait for user data hooks when Clerk says the user IS signed in.
  // Before Clerk loads, we only care about sdkAuth.isLoaded.
  // When not signed in, the data hooks fire (no `enabled` guard in the SDK)
  // and get 401 errors — we must not block on those responses.
  // Include subscriptionLoading so plan gating never sees a stale 'free' plan.
  const isLoading =
    !sdkAuth.isLoaded || (sdkAuth.isSignedIn === true && (userLoading || subscriptionLoading));
  const isAuthenticated = sdkAuth.isSignedIn ?? false;

  // Memoize journey instances transformation
  const journeyInstances = useMemo(() => buildJourneyInstances(journeys || []), [journeys]);

  // Memoize user object to prevent unnecessary re-renders
  const user = useMemo<IUser | null>(() => {
    if (!sdkAuth.user) return null;

    // Cast userProfile to extended type with optional business fields
    const extendedProfile = userProfile as typeof userProfile & {
      linkedinConnected?: boolean;
      linkedinProfile?: Record<string, unknown>;
      familyProgram?: IUser['familyProgram'];
      wallet?: IUser['wallet'];
    };

    // Subscription data comes from the billing API, not userProfile.
    // This ensures planGating.ts sees the real plan, not a stale 'free'.
    const plan = subscription?.plan || 'free';
    const proSubscriptionActive = subscription?.proSubscriptionActive ?? false;
    const familySubscriptionActive = subscription?.familyAddonActive ?? false;

    return {
      id: sdkAuth.user.id,
      email: sdkAuth.user.email,
      // The account's own name, or none — never a word standing in for one.
      firstName: sdkAuth.user.firstName ?? '',
      lastName: sdkAuth.user.lastName || '',
      plan,
      proSubscriptionActive,
      familySubscriptionActive,
      journeyInstances,
      linkedinConnected: extendedProfile?.linkedinConnected,
      linkedinProfile: extendedProfile?.linkedinProfile,
      role: mapSdkRoleToUserRole(sdkAuth.user.role),
      familyProgram: extendedProfile?.familyProgram,
      wallet: extendedProfile?.wallet,
      permissions: [],
    };
  }, [sdkAuth.user, userProfile, subscription, journeyInstances]);

  // Auth actions
  const login = useCallback(async (_email: string, _password: string) => {
    window.location.href = '/login';
  }, []);

  const signup = useCallback(
    async (_email: string, _password: string, _firstName: string, _lastName: string) => {
      window.location.href = '/signup';
    },
    []
  );

  const logout = useCallback(async () => {
    sessionStorage.removeItem('tonnie_redirect_after_auth');
    await clerkSignOut();
    setLocation('/login');
  }, [clerkSignOut, setLocation]);

  const updateUser = useCallback(
    (updates: Partial<IUser>) => {
      // Only pass supported SDK fields
      const sdkUpdates = {
        firstName: updates.firstName,
        lastName: updates.lastName,
      };
      updateProfileMutation.mutate(sdkUpdates);
    },
    [updateProfileMutation]
  );

  const addJourneyInstance = useCallback<IAuthContextValue['addJourneyInstance']>(
    (instance, options) => {
      // Build heroes enabled list from scope
      const heroesEnabled: string[] = [];
      if (instance.scope?.checkup) heroesEnabled.push('checkup');
      if (instance.scope?.teleportal) heroesEnabled.push('teleportal');
      if (instance.scope?.qultureup) heroesEnabled.push('qultureup');
      if (instance.scope?.care) heroesEnabled.push('care');
      if (instance.scope?.passport) heroesEnabled.push('passport');

      const hc = instance.householdConfig;

      // Cornerstone refactor 2026-05: clients no longer negotiate
      // funding source / tier at create time. The backend always
      // creates self_free journeys; the billing-webhook auto-upgrades
      // to self_pro on the next Stripe cycle if the user has Pro.
      // origin_country is now nullable (drop legacy 'UNK' sentinel).
      const payload = {
        originCountry: instance.originCountry ?? null,
        destinationCountry: instance.destinationCountry,
        scenario: instance.scenario,
        heroesEnabled,
        // Already the wire shape — derived through the one mapper by the
        // caller (F35). This used to re-map it here and drop `familySize`, so
        // the server defaulted every family journey to a household of one.
        householdConfig: hc,
        // The citizen's "Build": always a new journey (R2), idempotent on the
        // attempt's request id (R3). The server's advice was asked first.
        intent: 'new' as const,
        requestId: instance.requestId,
      };

      createJourneyMutation.mutate(payload, {
        onSuccess: () => {
          options?.onSuccess?.();
        },
        // Sentry capture is ALWAYS done here (single source of truth),
        // and the caller's `onError` is invoked afterwards so the UI
        // can reflect the failure (e.g. the journey-builder creation
        // banner). Before this, errors were ONLY captured to Sentry
        // and the user saw a false success screen — see
        // `useJourneyCreation` for the canonical consumer.
        onError: (error) => {
          const err = error instanceof Error ? error : new Error(String(error));
          captureException(err, {
            component: 'AuthContext',
            action: 'addJourneyInstance',
          });
          options?.onError?.(err);
        },
      });
    },
    [createJourneyMutation, captureException]
  );

  const resetPassword = useCallback(async (_email: string) => {
    window.location.href = '/forgot-password';
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<IAuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated,
      login,
      signup,
      logout,
      updateUser,
      addJourneyInstance,
      resetPassword,
    }),
    [
      user,
      isLoading,
      isAuthenticated,
      login,
      signup,
      logout,
      updateUser,
      addJourneyInstance,
      resetPassword,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook for checking if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated, isLoading } = useAuth();
  return !isLoading && isAuthenticated;
}

/**
 * Hook for requiring authentication - throws if not authenticated
 */
export function useRequireAuth(): IUser {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    throw new Error('Auth not loaded');
  }

  if (!isAuthenticated || !user) {
    throw new Error('User not authenticated');
  }

  return user;
}
